import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { LoadingModeType, LoadingVariantType } from '../../../types/loading.type';

const INLINE_SIZE = { width: 24, height: 30 };
const OVERLAY_SIZE = { width: 40, height: 50 };
/** Long enough that a fast request never flashes the loader. */
const DEFAULT_LOADER_DELAY_MS = 300;

@Component({
  selector: 'fr8bot-ui-loading',
  templateUrl: './loading.component.html',
  styleUrl: './loading.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.z-index]': "mode() === 'overlay' ? 999 : null",
    '[style.display]': "showLoader() ? 'flex' : 'none'",
    '[class.overlay-mode]': "mode() === 'overlay' && showLoader()",
    '[class.inline-mode]': "mode() === 'inline' && showLoader()",
    '[class.overlay-mode--variant-with-transparent]': "variant() === 'with-transparent'",
    '[class.overlay-mode--variant-not-transparent]': "variant() === 'not-transparent'",
  },
})
export class LoadingUIComponent implements OnInit {
  readonly mode = input<LoadingModeType>(LoadingModeType.Inline);
  readonly variant = input<LoadingVariantType>();
  readonly delayMs = input(DEFAULT_LOADER_DELAY_MS);
  readonly instant = input(false);
  readonly width = input(0);
  readonly height = input(0);

  readonly showLoader = signal(false);

  /** A zero input falls back to the size that matches the current mode. */
  readonly iconSize = computed(() => {
    const fallback = this.mode() === LoadingModeType.Inline ? INLINE_SIZE : OVERLAY_SIZE;

    return {
      width: this.width() || fallback.width,
      height: this.height() || fallback.height,
    };
  });

  private delayTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.delayTimer));
  }

  ngOnInit(): void {
    if (this.instant()) {
      this.showLoader.set(true);
      return;
    }

    this.delayTimer = setTimeout(() => this.showLoader.set(true), this.delayMs());
  }
}
